import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Mail, BellRing } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const isPast = (r) => r.archived === true || (r.archived !== false && (r.arrival_date || "") < todayStr());
export const hasYes = (r) => Object.values(r.responses || {}).some((x) => x.answer === "yes");

export const BrandLine = ({ r }) => {
  const brands = [...new Set(Object.values(r.brands || {}).filter(Boolean))].filter((b) => !(r.listing_title || "").includes(b));
  if (!brands.length) return null;
  return <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C6C50] mt-0.5" data-testid={`brand-line-${r.id}`}>{brands.join(" · ")}</div>;
};

export const ScopeTabs = ({ scope, setScope, rows, testid }) => {
  const past = rows.filter(isPast).length;
  return (
    <div className="inline-flex rounded-full border border-[#E5E1D8] bg-white p-1 mt-4" data-testid={testid}>
      {[["current", "En cours", rows.length - past], ["past", "Passées", past]].map(([k, l, n]) => (
        <button key={k} onClick={() => setScope(k)} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${scope === k ? "bg-[#2B3029] text-white" : "text-[#666] hover:text-[#1A1A1A]"}`} data-testid={`${testid}-${k}`}>
          {l} ({n})
        </button>
      ))}
    </div>
  );
};

export const NewAnswerBadge = ({ r, kind, onDone }) => {
  const n = (r.unseen || []).length;
  if (!n) return null;
  const seen = async () => {
    try {
      await axios.patch(`${API}/admin/${kind}/${r.id}/seen`, {}, { withCredentials: true });
      window.dispatchEvent(new Event("unseen-changed"));
      onDone();
    } catch (e) {
      toast.error("Action impossible");
    }
  };
  return (
    <button type="button" onClick={seen} title="Marquer comme vu" className="px-3 py-1.5 rounded-full bg-[#C2410C] text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-[#9A3412] transition-colors" data-testid={`new-answer-badge-${r.id}`}>
      <BellRing className="w-3.5 h-3.5" /> {n > 1 ? `${n} nouvelles réponses` : "Nouvelle réponse"} · marquer vu
    </button>
  );
};

export const ArchiveButton = ({ r, kind, onDone }) => {
  const past = isPast(r);
  const toggle = async () => {
    try {
      await axios.patch(`${API}/admin/${kind}/${r.id}/archive`, { archived: !past }, { withCredentials: true });
      toast.success(past ? "Demande remise en cours" : "Demande archivée");
      onDone();
    } catch (e) {
      toast.error("Action impossible");
    }
  };
  const Icon = past ? ArchiveRestore : Archive;
  return (
    <button type="button" onClick={toggle} className="px-3 py-2 rounded-md border border-[#E5E1D8] text-sm text-[#2B3029] inline-flex items-center gap-1.5 hover:bg-[#F5F3ED] transition-colors" data-testid={`archive-btn-${r.id}`}>
      <Icon className="w-3.5 h-3.5" /> {past ? "Désarchiver" : "Archiver"}
    </button>
  );
};

export const SendResponsesButton = ({ r, kind, onDone }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const post = (dry_run) => axios.post(`${API}/admin/${kind}/${r.id}/send-responses`, { note, dry_run }, { withCredentials: true });
  const refresh = async () => {
    try {
      const { data } = await post(true);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aperçu impossible");
      setOpen(false);
    }
  };
  const openDialog = () => { setOpen(true); setPreview(null); refresh(); };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await post(false);
      toast.success(`Réponses envoyées à ${data.to}`);
      setOpen(false);
      setNote("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };
  const last = (r.responses_sent || []).slice(-1)[0];
  return (
    <>
      <button type="button" onClick={openDialog} disabled={!hasYes(r)} title={hasYes(r) ? "" : "Aucune résidence n'a répondu Oui"} className="px-3 py-2 rounded-md bg-[#2B3029] text-white text-sm inline-flex items-center gap-1.5 hover:bg-[#1A1A1A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`send-responses-btn-${r.id}`}>
        <Mail className="w-3.5 h-3.5" /> Envoyer les réponses au client
      </button>
      {last && <div className="text-[11px] text-[#4F6B4A]" data-testid={`responses-sent-${r.id}`}>Réponses envoyées{last.auto ? " automatiquement" : ""} le {new Date(last.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}{last.emailed === false ? " · visible dans l'espace client, email non envoyé" : ""}</div>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-responses-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Envoyer les réponses au client</DialogTitle>
            <DialogDescription>À {r.user_email || r.email} · uniquement les résidences ayant répondu « Oui », classées par tarif.</DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#666]">Mot personnel (facultatif)</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} onBlur={refresh} placeholder="Ex. Je vous recommande la première, très bien desservie…" className="w-full mt-2 px-3 py-2 bg-[#F5F3ED] border border-[#E5E1D8] rounded-md text-sm focus:outline-none focus:border-[#8C6C50]" data-testid="send-responses-note" />
          </label>
          <div className="border border-[#E5E1D8] rounded-md overflow-hidden bg-white">
            {preview ? <iframe title="Aperçu" srcDoc={preview.html} className="w-full h-80" data-testid="send-responses-preview" /> : <div className="p-6 text-sm text-[#666]">Chargement de l'aperçu…</div>}
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm text-[#666] hover:text-[#1A1A1A]">Annuler</button>
            <button type="button" onClick={send} disabled={busy || !preview} className="px-4 py-2 rounded-md bg-[#8C6C50] text-white text-sm hover:bg-[#6B5B49] disabled:opacity-60" data-testid="send-responses-submit">
              {busy ? "Envoi…" : `Envoyer (${preview?.count ?? "…"} résidence${preview?.count > 1 ? "s" : ""})`}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
